import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Edit,
  Trash2,
  UserPlus,
  Eye
} from 'lucide-react';
import { 
  Button, 
  Card, 
  message,
  Space,
  Tooltip,
  Modal,
  Switch,
  Form,
  Input,
  Row,
  Col
} from 'antd';
import DashboardLayout from '../components/layout/DashboardLayout';
import CustomTable from '../components/common/CustomTable';
import api from '../config/api';
import dayjs from 'dayjs';

const Users = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Modal states
  const [addUserModal, setAddUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  
  // Form instances
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (values) => {
    try {
      setLoading(true);
      const response = await api.post('/users', {
        name: values.name,
        email: values.email,
        password: values.password
      });
      
      if (response.data.success) {
        message.success('User created successfully!');
        fetchUsers();
        setAddUserModal(false);
        addForm.resetFields();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      message.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (values) => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      const response = await api.put(`/users/${selectedUser._id}`, {
        name: values.name,
        email: values.email
      });
      
      if (response.data.success) {
        message.success('User updated successfully!');
        fetchUsers();
        setEditUserModal(false);
        setSelectedUser(null);
        editForm.resetFields();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      message.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      const response = await api.put(`/users/${selectedUser._id}/password`, {
        password: values.newPassword
      });
      
      if (response.data.success) {
        message.success('Password updated successfully!');
        setChangePasswordModal(false);
        setSelectedUser(null);
        passwordForm.resetFields();
      }
    } catch (error) {
      console.error('Error updating password:', error);
      message.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    
    try {
      setLoading(true);
      const response = await api.put(`/users/${userId}/${action}`);
      if (response.data.success) {
        message.success(`User ${action}d successfully!`);
        fetchUsers();
      }
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      message.error(`Failed to ${action} user`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    Modal.confirm({
      title: 'Delete User',
      content: (
        <div>
          <p>Are you sure you want to delete user "<strong>{user.name}</strong>"?</p>
          <p className="text-red-600 mt-2">⚠️ This action cannot be undone.</p>
        </div>
      ),
      okText: 'Delete User',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.delete(`/users/${user._id}`);
          if (response.data.success) {
            message.success('User deleted successfully!');
            fetchUsers();
          }
        } catch (error) {
          console.error('Error deleting user:', error);
          message.error(error.response?.data?.message || 'Failed to delete user');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Responsive columns
  const columns = [
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text, record) => (
        <div>
          <div className="font-medium text-xs">{text}</div>
          <div className="text-xs text-gray-500 truncate">{record.email}</div>
          {record._id === currentUser?.id && (
            <span className="text-xs text-blue-600 font-medium">(You)</span>
          )}
        </div>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
      responsive: ['md'],
      render: (email) => (
        <Tooltip title={email}>
          <span className="text-xs">{email}</span>
        </Tooltip>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleUserStatus(record._id, isActive)}
          disabled={record._id === currentUser?.id}
          size="small"
        />
      )
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: 120,
      responsive: ['lg'],
      render: (date) => (
        <span className="text-xs">
          {date ? dayjs(date).format('DD/MM/YY HH:mm') : 'Never'}
        </span>
      )
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      responsive: ['xl'],
      render: (date) => (
        <span className="text-xs">
          {dayjs(date).format('DD/MM/YY')}
        </span>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Edit User">
            <Button 
              type="text" 
              size="small"
              icon={<Edit size={12} />}
              className="!p-1 !min-w-0"
              onClick={() => {
                setSelectedUser(record);
                editForm.setFieldsValue({
                  name: record.name,
                  email: record.email
                });
                setEditUserModal(true);
              }}
            />
          </Tooltip>
          
          <Tooltip title="Change Password">
            <Button 
              type="text" 
              size="small"
              icon={<Eye size={20} />}
              className="!p-1 !min-w-0"
              onClick={() => {
                setSelectedUser(record);
                setChangePasswordModal(true);
              }}
            />
          </Tooltip>
          
          {record._id !== currentUser?.id && (
            <Tooltip title="Delete User">
              <Button 
                type="text" 
                size="small"
                danger
                icon={<Trash2 size={12} />}
                className="!p-1 !min-w-0"
                onClick={() => handleDeleteUser(record)}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const headerActions = (
    <Button 
      type="primary" 
      icon={<UserPlus size={14} />}
      size="small"
      onClick={() => setAddUserModal(true)}
    >
      <span className="hidden sm:inline text-xs">Add User</span>
    </Button>
  );

  const breadcrumbs = [
    { title: 'Dashboard', onClick: () => navigate('/dashboard') },
    { title: 'Users Management' }
  ];

  return (
    <DashboardLayout  
      title="Users Management" 
      subtitle="Manage system users and permissions"
      headerActions={headerActions}
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-4 m-4">
        <Card size="small" className="overflow-hidden">
          <CustomTable
            columns={columns}
            dataSource={users}
            loading={loading}
            scroll={{ x: 'max-content', y: 350 }}
            pagination={{
              pageSize: 20,
              showTotal: (total, range) => (
                <span className="text-xs">
                  {window.innerWidth >= 640 
                    ? `${range[0]}-${range[1]} of ${total} users`
                    : `${total} total`
                  }
                </span>
              )
            }}
          />
        </Card>
      </div>

      {/* Add User Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Add New User</span>}
        open={addUserModal}
        onCancel={() => {
          setAddUserModal(false);
          addForm.resetFields();
        }}
        footer={null}
        width="min(90vw, 600px)"
        centered
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleAddUser}
          className="mt-4"
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={<span className="text-xs font-medium">Full Name</span>}
                rules={[
                  { required: true, message: 'Please enter full name' },
                  { min: 2, message: 'Name must be at least 2 characters' }
                ]}
              >
                <Input placeholder="Enter full name" className="text-xs" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label={<span className="text-xs font-medium">Email Address</span>}
                rules={[
                  { required: true, message: 'Please enter email address' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input placeholder="Enter email address" className="text-xs" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="password"
                label={<span className="text-xs font-medium">Password</span>}
                rules={[
                  { required: true, message: 'Please enter password' },
                  { min: 6, message: 'Password must be at least 6 characters' }
                ]}
              >
                <Input.Password placeholder="Enter password" className="text-xs" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="confirmPassword"
                label={<span className="text-xs font-medium">Confirm Password</span>}
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm password" className="text-xs" />
              </Form.Item>
            </Col>
          </Row>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              onClick={() => {
                setAddUserModal(false);
                addForm.resetFields();
              }}
              size="small"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} size="small">
              <span className="text-xs">Create User</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Edit User</span>}
        open={editUserModal}
        onCancel={() => {
          setEditUserModal(false);
          setSelectedUser(null);
          editForm.resetFields();
        }}
        footer={null}
        width="min(90vw, 600px)"
        centered
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditUser}
          className="mt-4"
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={<span className="text-xs font-medium">Full Name</span>}
                rules={[
                  { required: true, message: 'Please enter full name' },
                  { min: 2, message: 'Name must be at least 2 characters' }
                ]}
              >
                <Input placeholder="Enter full name" className="text-xs" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label={<span className="text-xs font-medium">Email Address</span>}
                rules={[
                  { required: true, message: 'Please enter email address' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input placeholder="Enter email address" className="text-xs" />
              </Form.Item>
            </Col>
          </Row>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              onClick={() => {
                setEditUserModal(false);
                setSelectedUser(null);
                editForm.resetFields();
              }}
              size="small"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} size="small">
              <span className="text-xs">Update User</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Change Password</span>}
        open={changePasswordModal}
        onCancel={() => {
          setChangePasswordModal(false);
          setSelectedUser(null);
          passwordForm.resetFields();
        }}
        footer={null}
        width="min(90vw, 500px)"
        centered
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
          className="mt-4"
        >
          <div className="bg-blue-50 p-3 rounded-lg mb-4">
            <p className="text-blue-800 font-medium text-xs">
              Changing password for: {selectedUser?.name}
            </p>
            <p className="text-blue-600 text-xs">
              {selectedUser?.email}
            </p>
          </div>

          <Form.Item
            name="newPassword"
            label={<span className="text-xs font-medium">New Password</span>}
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password placeholder="Enter new password" className="text-xs" />
          </Form.Item>

          <Form.Item
            name="confirmNewPassword"
            label={<span className="text-xs font-medium">Confirm New Password</span>}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" className="text-xs" />
          </Form.Item>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              onClick={() => {
                setChangePasswordModal(false);
                setSelectedUser(null);
                passwordForm.resetFields();
              }}
              size="small"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} size="small">
              <span className="text-xs">Update Password</span>
            </Button>
          </div>
        </Form>
      </Modal>
    </DashboardLayout>
  );
};

export default Users;